import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../config/axios';
import { relativeSince } from '../../utils/relativeSince';
import { businessLabel } from '../../utils/businessType';

export const ALERTS_POLL_MS = 60000;

const TYPE_LABEL = {
  // account activation: a verified signup with no subscription yet (company alert, no device)
  awaiting_activation: 'New account awaiting activation · حساب جديد بانتظار التفعيل',
  audio_stall: 'No sound — audio output stalled',
  audio_routing_unavailable: 'USB audio routing unavailable',
  // build 404 -- an item froze and the player skipped it (the exact label requested; the item's name follows on the second line)
  item_stalled: 'تعذّر تشغيل مادة',
  // build 413 -- playback stopped moving (the video freeze ladder / watchdog reported playback.stalled and nothing closed it); the second line says whether it is repairing itself
  playback_stalled: 'توقّف التشغيل',
  // build 415 -- the internet (or YouTube) is unreachable; the second line says which, and that the screen keeps showing what is stored on it
  playback_outage: 'انقطاع الإنترنت',
  // build 405 -- the connected USB sound device is faulty and the player suspended its repairs (the product name follows on the second line)
  external_fault: 'جهاز الصوت الموصول لا يعمل بشكل صحيح',
};

export function alertTypeLabel(type) {
  return TYPE_LABEL[type] || String(type || 'Alert').replace(/_/g, ' ');
}

/** Header bell: polls GET /admin/alerts every 60 s, badge = count (hidden at 0), dropdown links each alert to the device health page. */
export default function AlertBell() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(() => {
    adminApi.get('/alerts')
      .then((res) => {
        const list = res?.data?.data?.alerts;
        setAlerts(Array.isArray(list) ? list : []);
      })
      .catch(() => { /* keep the last list; the next poll retries */ });
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, ALERTS_POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const count = alerts.length;

  return (
    <div className="relative" ref={ref}>
      <button
        className="btn-icon relative"
        title="Notifications"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.438L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {count > 0 && (
          <span
            data-testid="alert-badge"
            className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-[10px] leading-4 text-center font-semibold"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="alert-dropdown" className="absolute right-0 mt-2 w-80 card shadow-xl py-1 z-50 animate-fade-in">
          {count === 0 ? (
            <p className="px-4 py-3 text-sm text-text-muted">No alerts</p>
          ) : (
            alerts.map((a, i) => (
              <button
                key={`${a.type}-${a.device_id || a.company_id}-${i}`}
                data-testid="alert-item"
                onClick={() => { setOpen(false); navigate(a.link || '/device-health'); }}
                className="w-full text-left px-4 py-2.5 hover:bg-white/[0.04] transition-colors border-b border-white/[0.06] last:border-b-0"
              >
                <p dir="auto" className="text-sm font-medium text-text-main">{alertTypeLabel(a.type)}</p>
                <p className="text-xs text-text-muted truncate">
                  {a.type === 'item_stalled' && a.detail ? `Item: ${a.detail} · ` : ''}
                  {a.type === 'external_fault' && a.detail ? `Device: ${a.detail} · ` : ''}
                  {(a.type === 'playback_stalled' || a.type === 'playback_outage') && a.detail ? `${({ repairing: 'Repairing itself', gave_up: 'Gave up — needs a restart', observe_only: 'Detected (observing only)', outage_full: 'No internet — showing stored content', outage_youtube: 'YouTube unreachable — showing stored content' })[a.detail] ?? a.detail} · ` : ''}
                  {a.type === 'awaiting_activation' ? '' : (a.device_name || a.serial || a.device_id)}
                  {a.type === 'awaiting_activation' ? (a.company_name || '') : (a.company_name ? ` · ${a.company_name}` : '')}
                  {a.type === 'awaiting_activation' && a.owner_email ? ` · ${a.owner_email}` : ''}
                  {a.type === 'awaiting_activation' && a.business_category ? ` · ${businessLabel(a.business_category, a.business_category_other)}` : ''}
                  {relativeSince(a.since) ? ` · since ${relativeSince(a.since)}` : ''}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
